/**
 * Which course portal a returning learner lands in front of in Quikbase (QUI-166; Sol quikbase-spawn A,
 * audit-quikbase-spawn).
 *
 * Unity applies `shift_spawn_point_to_curriculum_portal` only at the moment the avatar lands, which on a
 * cached load is before the world's own course fetch returns - so the target must be known synchronously:
 *   1. the grade and subject the backend signed into the launch token for the token's own course;
 *   2. only for a token issued before those claims existed (no courseContextVersion): the grade and
 *      subject in the exit-to-Quikbase address - an unsigned hint, never used for a current token;
 *   3. the course store, but only once it holds the token's own course.
 * Otherwise null: land centrally. Never the course store's placeholder (grade 2 Science).
 *
 * @param {{token?: {courseId?: string, grade?: string|number, subject?: string, courseContextVersion?: number},
 *          url?: {grade?: string, subject?: string},
 *          store?: {courseId?: string, grade?: string|number, subject?: string}}} sources
 * @returns {{grade: string, subject: string, source: 'token'|'url'|'store'} | null}
 */
export function quikbaseSpawnTarget({token, url, store} = {}) {
  const issuedBeforeClaims = token?.courseContextVersion == null;
  const storeHoldsTokenCourse = Boolean(token?.courseId) && store?.courseId === token.courseId;
  return (
    portal(token, 'token') ||
    (issuedBeforeClaims ? portal(url, 'url') : null) ||
    (storeHoldsTokenCourse ? portal(store, 'store') : null)
  );
}

// A portal is named by a grade (K or a number) and a one-word subject: the Unity command is space-separated.
const GRADE = /^(?:K|\d{1,2})$/i;
const SUBJECT = /^[A-Za-z]{2,32}$/;

function portal(from, source) {
  const grade = from?.grade == null ? '' : String(from.grade).trim();
  const subject = from?.subject == null ? '' : String(from.subject).trim();
  return GRADE.test(grade) && SUBJECT.test(subject) ? {grade, subject, source} : null;
}

/** The Unity command that lands the learner in front of `target`'s portal. */
export const spawnShiftCommand = target =>
  `shift_spawn_point_to_curriculum_portal ${target.grade} ${target.subject}`;
